/**
 * VersionDropdown Component
 * Visar systemversion med expanderbar dropdown för detaljer
 * Varje version kan expanderas för att visa release notes
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';

const CURRENT_VERSION = '5.0.0';

interface ReleaseInfo {
  version: string;
  date: string;
  status: 'released' | 'current' | 'upcoming';
  highlights: string[];
}

const RELEASES: ReleaseInfo[] = [
  {
    version: '5.0.1',
    date: '',
    status: 'upcoming',
    highlights: [
      'version.release.upcoming.drivingLogApp',
    ],
  },
  {
    version: '5.0.0',
    date: '2026-03-30',
    status: 'current',
    highlights: [
      'version.release.v500.newPlatform',
      'version.release.v500.dashboard',
      'version.release.v500.fleet',
      'version.release.v500.liveTracking',
      'version.release.v500.reports',
      'version.release.v500.analysis',
      'version.release.v500.crm',
      'version.release.v500.geofences',
      'version.release.v500.homecare',
      'version.release.v500.deviceManagement',
      'version.release.v500.admin',
      'version.release.v500.rbac',
      'version.release.v500.osmMaps',
    ],
  },
];

// Expanderbar version-komponent
const VersionItem: React.FC<{
  release: ReleaseInfo;
  isExpanded: boolean;
  onToggle: () => void;
  t: (key: string) => string;
}> = ({ release, isExpanded, onToggle, t }) => {
  const getStatusBadge = () => {
    switch (release.status) {
      case 'current':
        return (
          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded">
            {t('version.current') || 'Aktuell'}
          </span>
        );
      case 'upcoming':
        return (
          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded">
            {t('version.upcoming') || 'Kommande'}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="border-b border-border last:border-b-0">
      {/* Version Header - Klickbar */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-bgLight/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="font-mono font-semibold text-text-default">v{release.version}</span>
          {getStatusBadge()}
        </div>
        <div className="flex items-center gap-2">
          {release.date && (
            <span className="text-xs text-text-muted">{release.date}</span>
          )}
          {/* Chevron */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 text-text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanderad innehåll */}
      {isExpanded && (
        <div className="px-4 pb-3 pt-1">
          <div className="space-y-2 pl-2 border-l-2 border-primary/30">
            {release.highlights.map((key, index) => (
              <div key={index} className="flex items-start gap-2 text-sm pl-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 flex-shrink-0 mt-0.5 ${
                    release.status === 'upcoming' ? 'text-blue-400' : 'text-green-500'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  {release.status === 'upcoming' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  )}
                </svg>
                <span className="text-text-muted">{t(key) || key.split('.').pop()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const VersionDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set([CURRENT_VERSION]));
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Stäng dropdown vid klick utanför
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleVersion = (version: string) => {
    setExpandedVersions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(version)) {
        newSet.delete(version);
      } else {
        newSet.add(version);
      }
      return newSet;
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Version Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="h-8 px-2 text-xs cursor-pointer rounded-full hover:bg-bgLight transition-colors flex items-center justify-center gap-1 text-text-muted hover:text-text-default"
        aria-label={t('version.title') || 'Version'}
        title={`${t('version.title') || 'Version'}: ${CURRENT_VERSION}`}
      >
        {/* Info/Version Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
        <span className="font-mono font-medium">v{CURRENT_VERSION}</span>
        {/* Chevron */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-bgContainer rounded-lg shadow-lg border border-border z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-4 py-3 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-text-default">DQFleet</h3>
                  <p className="text-xs text-text-muted">{t('version.fleetManagement') || 'Fleet Management System'}</p>
                </div>
              </div>
              <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-mono rounded">
                v{CURRENT_VERSION}
              </span>
            </div>
          </div>

          {/* Versions List - Scrollable */}
          <div className="max-h-80 overflow-y-auto">
            <div className="px-4 py-2 bg-bgLight/30 border-b border-border">
              <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide">
                {t('version.releaseHistory') || 'Versionshistorik'}
              </h4>
            </div>
            {RELEASES.map((release) => (
              <VersionItem
                key={release.version}
                release={release}
                isExpanded={expandedVersions.has(release.version)}
                onToggle={() => toggleVersion(release.version)}
                t={t}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-bgLight/50 border-t border-border">
            <div className="flex items-center justify-center text-xs text-text-muted">
              <span>© 2026 Deviaq AB</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionDropdown;

